/*
 * PAINT AT MOST 60 TIMES A SECOND, KEEP THE PHYSICS AT ITS OLD PACE (2026-10-18, Chris: "There is DEFINITELY STILL SKIPPING AND CHOPPINESS... The demo (and staging and Prod) MUST be smooth
 * all the time"). Measured on this Mac, which is the machine he looks at it on: its screen refreshes at 120 Hz (the page's requestAnimationFrame gaps are 8.3 ms, p50, in the app's own
 * browser pane), and a headless Chrome - what every earlier check used - runs a flat 60. At 120 Hz a frame has 8.3 ms, not 16.7, and the dashboard's per-frame work (the bubble
 * simulation, the ~100 position writes that follow it, the style recalculation those writes cause, the paint) is the same work in half the time. In a real, headed Chrome the plain
 * dashboard held 118 fps at 28-40% of the main thread, but with icons, flow lines, activity and the drawer on it fell to 87-110 fps with 17 ms frames: the stagger he describes.
 *
 * d3-force runs its simulation from a d3 timer, which fires on EVERY animation frame and does one physics step and one repaint of the bubbles each time. This module takes that timer
 * over for a simulation: it does the same physics steps, at the same pace, but turns them into ONE repaint per ~16.7 ms however fast the screen is.
 *   - the physics is stepped at 120 a second (the rate it ran at on a 120 Hz screen, so the bubbles settle and move at the same speed as before; on a 60 Hz screen it now takes two steps a
 *     frame, which is what the display Chris tuned it on always did), and
 *   - the "tick" listener (the repaint) runs once per frame that is at least ~12.5 ms after the last, so a 120 Hz screen paints 60 times a second and a 144 Hz one 72.
 *
 * It replaces only the timer. `restart`, `stop`, the "tick" and "end" listeners, `alpha` and the forces all behave as d3's, and `sim.tick(n)` called by hand (the silent settle) is d3's.
 */

/** the rate the bubble physics has always effectively run at on this display; one step is this long */
export const PHYSICS_STEP_MS = 1000 / 120;
/** the fastest the repaint is allowed to run (a frame earlier than this after the last is skipped) */
export const PAINT_FPS = 60;
/** a frame that comes sooner than this after the last paint is skipped: 75% of a 60 Hz frame, so a 60 Hz screen never loses a frame to timer jitter and a 120 Hz one paints every second */
export const MIN_PAINT_GAP_MS = (0.75 * 1000) / PAINT_FPS;

/*
 * WHEN SKIPPING IS ALLOWED (2026-10-20, Chris's screen recording "skipping.mov": a bubble crossing between boxes advanced about 5 px a paint and stalled every fourth captured frame, which is a
 * page painting at 45 a second). The gap rule above is only right on a screen that is a clean 60 Hz or 120 Hz: on ANY screen in between (an adaptive ProMotion display, a power-saving mode, a
 * 90 or 100 Hz monitor) it still throws away every second frame, so MEASURED with this pacer: 80 Hz paints 50 a second, UNEVENLY (spacing varies by 6 ms), 90 Hz paints 45, 100 Hz paints 50, 110 Hz
 * paints 55. Headless Chrome is a flat 60 Hz and the 120 Hz check only ever saw 60 and 120, which is how it passed. So: skip frames ONLY when the screen is fast enough that skipping still leaves
 * a full 60 paints a second (about 116 Hz and up); below that every frame is painted, however many there are. The screen's rate is the median spacing of its recent frames (a stall does not count),
 * with a small band between the two thresholds so a screen sitting near the line does not flip back and forth.
 */
/** frames needed before the screen's rate is trusted; until then the original rule applies */
export const REFRESH_SAMPLES = 12;
/** how many recent frame gaps the median is taken over */
export const REFRESH_WINDOW = 31;
/** a screen whose median frame gap is under this (about 116 Hz and up) may be painted every second frame */
export const SKIP_BELOW_GAP_MS = 8.6;
/** ...and goes back to painting every frame once the median gap is over this (slower than about 111 Hz) */
export const PAINT_ALL_ABOVE_GAP_MS = 9.0;
/** a gap longer than this is a stall (a hidden tab, a long task), not the screen's rate */
export const STALL_GAP_MS = 100;

/** what is learned about the screen, shared by every paced simulation (they restart constantly, and all run on the same display) */
const screen = { gaps: [] as number[], skipping: true };
/** forget what was learned (tests; a display change is picked up by itself within a second) */
export const resetScreenEstimate = () => {
  screen.gaps = [];
  screen.skipping = true;
};
/** is the pacer currently skipping in-between frames? (for tests and the debug readout) */
export const isSkippingFrames = () => screen.skipping;
const median = (xs: number[]): number => {
  const sorted = [...xs].sort((a, b) => a - b);

  return sorted[Math.floor(sorted.length / 2)];
};
/** feed one frame gap; update whether skipping is allowed */
export const noteFrameGap = (gapMs: number) => {
  if (gapMs <= 0 || gapMs > STALL_GAP_MS) return;
  screen.gaps.push(gapMs);
  if (screen.gaps.length > REFRESH_WINDOW) screen.gaps.shift();
  if (screen.gaps.length < REFRESH_SAMPLES) return;
  const m = median(screen.gaps);
  if (screen.skipping && m > PAINT_ALL_ABOVE_GAP_MS) screen.skipping = false;
  else if (!screen.skipping && m < SKIP_BELOW_GAP_MS) screen.skipping = true;
};
/** after a stall (a hidden tab, a long task) take at most this many steps at once: catching up in one go would be a visible jump, and a spiral if the steps are what is slow */
export const MAX_STEPS_PER_PAINT = 6;

/** what d3.forceSimulation returns that this needs */
export interface PaceableSimulation {
  tick(iterations?: number): unknown;
  stop(): unknown;
  restart(): unknown;
  alpha(): number;
  alphaMin(): number;
  on(typenames: string): ((this: unknown) => void) | undefined;
}

export interface FrameScheduler {
  request(callback: (now: number) => void): number;
  cancel(handle: number): void;
}

const browserScheduler: FrameScheduler = {
  request: (callback) => window.requestAnimationFrame(callback),
  cancel: (handle) => window.cancelAnimationFrame(handle),
};

/*
 * SNAP: a paint owes a whole number of steps, and a paint gap that is a whole number of steps plus or minus timing jitter must owe THAT number every time. It used to round DOWN, so at 60 paints a second
 * (exactly two steps) a jitter of 0.05 ms flipped the answer between 1, 2 and 3 steps in a 1:3:1 mix and a bubble's distance per paint swung by a third (measured 2026-10-20, Chris's second recording:
 * "It stutters ... even at spawn"). Within SNAP of the next whole step counts as reaching it, and the small debt this leaves is carried (and repaid) like any remainder.
 */
export const STEP_SNAP = 0.2;

/** perf/step-regularity.mjs sets window.__pacerStepLog to an array to read how many steps each paint took (nothing otherwise) */
const stepLog = (): number[] | undefined => (typeof window !== "undefined" ? (window as unknown as { __pacerStepLog?: number[] }).__pacerStepLog : undefined);

/** How many physics steps a paint that comes `sinceLastPaintMs` after the last owes, given what was left over from the last one. Pure, so the pacing rule can be tested on its own. */
export const stepsOwed = (sinceLastPaintMs: number, carryMs: number): { steps: number; carryMs: number } => {
  const budget = carryMs + Math.max(0, sinceLastPaintMs);
  const whole = Math.floor(budget / PHYSICS_STEP_MS + STEP_SNAP);
  const steps = Math.min(MAX_STEPS_PER_PAINT, Math.max(1, whole));
  // keep the remainder only when nothing was capped: after a stall the lost time is dropped, not owed
  const left = whole > MAX_STEPS_PER_PAINT ? 0 : Math.max(-PHYSICS_STEP_MS, budget - steps * PHYSICS_STEP_MS);

  return { steps, carryMs: left };
};

export interface PaceOptions {
  /**
   * Asked when the simulation has cooled (alpha under alphaMin), where the loop would stop. True keeps it painting, WITHOUT stepping the physics, until it says false: a bubble's slow glide
   * to where the simulation put it (bubbleGlide.ts, Medium and Slow speeds) goes on for seconds after the simulation has stopped, and a paint is what moves it.
   */
  keepPainting?: () => boolean;
}

/**
 * Take over `sim`'s timer. d3 starts the simulation's timer as it is created, so this stops that first and then makes `restart` and `stop` drive a paced frame loop instead. Returns the same
 * simulation object (so a call chain like `.alphaTarget(0.3).restart()` still works) and a function that reports whether the loop is running (for tests and the debug readout).
 */
export const paceSimulation = <T extends PaceableSimulation>(sim: T, scheduler: FrameScheduler = browserScheduler, options: PaceOptions = {}): T & { pacedRunning: () => boolean } => {
  const paced = sim as T & { pacedRunning: () => boolean };
  let handle = 0;
  let running = false;
  let lastPaint: number | null = null;   // null, not 0: a frame timestamp of 0 is a real time
  let lastFrame: number | null = null;   // the previous animation frame (painted or not), to measure the screen's rate
  let carry = 0;

  const halt = () => {
    if (running) scheduler.cancel(handle);
    running = false;
    handle = 0;
  };

  const frame = (now: number) => {
    if (!running) return;
    handle = scheduler.request(frame);
    if (lastFrame !== null) noteFrameGap(now - lastFrame);
    lastFrame = now;
    if (screen.skipping && lastPaint !== null && now - lastPaint < MIN_PAINT_GAP_MS) return;     // a 120 Hz screen's in-between frame: nothing to do
    // cooled already: only a glide still in flight is being painted (see keepPainting), and the physics has nothing left to step
    const cooled = lastPaint !== null && sim.alpha() < sim.alphaMin();
    if (cooled) {
      carry = 0;
    } else {
      const owed = stepsOwed(lastPaint !== null ? now - lastPaint : PHYSICS_STEP_MS, carry);
      carry = owed.carryMs;
      stepLog()?.push(owed.steps);
      sim.tick(owed.steps);
    }
    lastPaint = now;
    sim.on("tick")?.call(sim);
    if (sim.alpha() < sim.alphaMin() && !options.keepPainting?.()) {
      halt();
      sim.on("end")?.call(sim);
    }
  };

  sim.stop();                                                         // d3's own timer, started at creation
  paced.restart = () => {
    if (!running) {
      running = true;
      lastPaint = null;
      lastFrame = null;   // the time the loop was stopped is not a frame gap
      carry = 0;
      handle = scheduler.request(frame);
    }

    return paced;
  };
  paced.stop = () => {
    halt();

    return paced;
  };
  paced.pacedRunning = () => running;

  return paced;
};

/** A diagnostic switch for measuring with and without the pacing: `localStorage.setItem("skydive.noPace", "1")` and reload. Anything else (or storage that cannot be read) leaves the pacing on. */
export const pacingSwitchedOff = (): boolean => {
  try {
    return window.localStorage.getItem("skydive.noPace") === "1";
  } catch {
    return false;
  }
};
